<template>
  <div id="app">
    <StartupProcessComponent />
    <component :is="layout" v-if="pageReady" @vue:mounted="contentsReady"/>
    <AppErrorWatchDialog />
    <SessionCheckerComponent/>
    <ModalsContainer />
  </div>
</template>

<script lang="ts">
export default {
  name: 'App',
}
</script>

<script setup lang="ts">
  import { computed, watch, nextTick, onMounted, onBeforeUnmount, onErrorCaptured, defineAsyncComponent } from 'vue'
  import { useRoute } from 'vue-router'
  import { errorDefaultMessage } from '@/utils/errors'
  import * as loggers from "@/utils/loggers"
  import { ModalsContainer } from 'vue-final-modal'
  import { useVersionChecksStore } from "@/stores/versionChecks"
  import { useSessionIdStore } from "@/stores/sessionId"
  import { useLoginStore } from '@/stores/login'
  import { useAppErrorStore } from "@/stores/appError"
  import AppErrorWatchDialog from "@/components/cloud/error/AppErrorWatchDialog.vue"
  const loginStore = useLoginStore()
  const versionChecksStore = useVersionChecksStore()
  const sessionIdStore = useSessionIdStore()
  const appErrorStore = useAppErrorStore()
  const route = useRoute()

  // components
  const SessionCheckerComponent = defineAsyncComponent(() => import("@/components/cloud/session/SessionCheckerComponent.vue"))
  const StartupProcessComponent = defineAsyncComponent(() => import("@/components/cloud/notification/StartupProcessComponent.vue"))

  // computed
  const layout = computed(() => {
    return (route.meta.layout || 'default') + '-layout';
  })
  const accountInfo = computed(() => {
    return loginStore.currentAccount
  })
  const pageReady = computed(() => {
    if(!route.name) return false;
    if(route.meta.isPublic) return true;
    if(!accountInfo.value) return false;
    if(!accountInfo.value.account_id) return false;
    return true;
  })
  // watch
  watch(
    () => pageReady.value,
    (pageReady) => {
      if(pageReady){
        const sessionId = sessionIdStore.getSessionId();
        if(sessionId) {
          loggers.setSessionId(sessionId.substring(0, 8))
        }
        if(accountInfo.value){
          loggers.setAccountId(accountInfo.value.account_id)
        }
        loggers.setKuraemonCloudWebPageVersion(versionChecksStore.storedKuraemonCloudWebPageVersion)

        loggers.info(`page ready`)
      }
    },
  )
  // methods
  const contentsReady = () => {
    nextTick(() => { 
      const initialLoading = document.querySelector(".initial-loading-wrap") as HTMLElement
      if(initialLoading){
        initialLoading.style.display = 'none'
      }
    })
  }
  const handleUnhandledrejection = (e: PromiseRejectionEvent) => {
    console.warn('unhandledrejection', e)
    loggers.warn("unhandledrejection", {
      kCloudAppError: {
        error: e,
        reason: (e.reason ? e.reason.toString() : e.toString()),
      }
    })
  }
  onMounted(() => {
    window.addEventListener('unhandledrejection', handleUnhandledrejection)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('unhandledrejection', handleUnhandledrejection)
  })
  onErrorCaptured((err, vm, info) => {
    console.warn(err, vm, info)
    appErrorStore.updateAppError({
      error: err,
      customErrorCode: 'PE00',
      description: errorDefaultMessage
    })
    return true;
  })
   // created
  const created = async() => {
    // リリースバージョンのチェック
    await versionChecksStore.checkWebPageVersion();
  }
  created()
</script>

<style>
html {
  box-sizing: border-box;
  font-size: 14px;
  font-family: Meiryo, sans-serif;
  height: 100%;
}
body {
  margin: 0;
  font-size: 1rem;
  height: 100%;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}
#app {
  height: 100%;
}
button,
input,
select,
textarea {
  font-family: inherit;
}
button {
  outline: none;
}
*,
*::before,
*::after {
  box-sizing: inherit;
}
</style>
